/**
 * DRM 再生の現地診断ログ。
 *
 * 「音は出るが映像が黒い」等の環境依存トラブル（HDCP/出力保護・キーシステム
 * 選択ミスマッチ）を、利用者環境の devtools コンソールだけで切り分けるための
 * 軽量ログを出す。トークン等の秘匿情報はログしない。
 *
 * `localStorage.gokuriDrmDebug = '1'` で詳細ログ（video 要素の状態スナップ
 * ショット等）を有効化できる。
 */
import { DrmProfile } from './streaming-url.utils';

const LOG_PREFIX = '[drm-diag]';

export function isDrmDebugEnabled(): boolean {
  try {
    return localStorage.getItem('gokuriDrmDebug') === '1';
  } catch {
    return false;
  }
}

export interface DrmDiagnosticsSession {
  /** MKPlayer インスタンス生成直後に呼ぶ（対応 DRM 一覧をログ） */
  onPlayerCreated(player: unknown): void;
  /** MKPlayerEvent.DrmLicenseAdded のハンドラ（実際に選ばれたキーシステムをログ） */
  onLicenseAdded(event: unknown): void;
  /** MKPlayerEvent.Error のハンドラ（コード・詳細を全量ログ） */
  onError(event: unknown): void;
  /** <video> 要素検出時に呼ぶ（再生開始後に描画状態をスナップショット） */
  onVideoElement(video: HTMLVideoElement): void;
}

/**
 * 再生セッション 1 回分の診断ロガーを開始する。
 */
export function startDrmDiagnostics(
  label: string,
  manifestUrl: string,
  profile: DrmProfile,
): DrmDiagnosticsSession {
  const tag = `${LOG_PREFIX}[${label}]`;

  console.info(`${tag} session start`, {
    manifestUrl,
    profile,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a',
  });

  // HDCP プローブ: ソフトウェア CDM (Widevine L3) は HDCP を証明できないため、
  // 正常再生できる環境でも output-restricted を返しうる（Mac Chrome で確認済み）。
  // 参考値であり、原因確定には下の実セッションのキーステータスを使う。
  probeHdcpStatus().then((status) => {
    console.info(`${tag} HDCP probe (minHdcpVersion=1.4, 参考値):`, status);
  });

  // 実セッションのキーステータス（usable / output-restricted 等）を記録する。
  // 「音のみ・映像黒」の確定診断はこちら: 映像キーだけ output-restricted なら
  // ライセンスポリシーの出力保護要求が原因。
  installEmeKeyStatusHook(tag);

  let videoSnapshotDone = false;

  return {
    onPlayerCreated(player: unknown): void {
      const drmApi = (player as any)?.drm;
      drmApi?.getSupportedDRM?.()
        .then((systems: string[]) => console.info(`${tag} supported DRM:`, systems))
        .catch(() => { /* 取得失敗は無視 */ });
    },

    onLicenseAdded(event: unknown): void {
      const license = (event as any)?.license;
      console.info(`${tag} license added:`, {
        keySystemString: license?.keySystemString,
        keyIds: license?.keyIds,
      });
    },

    onError(event: unknown): void {
      const e = event as any;
      console.error(`${tag} player error:`, {
        code: e?.code ?? e?.data?.code,
        name: e?.name,
        message: e?.message,
        data: e?.data,
        sourceIdentifier: e?.sourceIdentifier,
      });
    },

    onVideoElement(video: HTMLVideoElement): void {
      if (videoSnapshotDone) return;
      videoSnapshotDone = true;
      // 再生が立ち上がった頃合いに 1 回だけ描画状態を記録する。
      // videoWidth=0 や droppedFrames=totalFrames は「デコードはしているが
      // 描画できていない」（出力保護/HW デコード問題）のサイン。
      const snapshot = () => logVideoElementState(tag, video);
      video.addEventListener('playing', () => setTimeout(snapshot, 3000), { once: true });
      if (isDrmDebugEnabled()) {
        setTimeout(snapshot, 5000);
      }
    },
  };
}

/**
 * Widevine の MediaKeys を一時生成し、HDCP 1.4 を要求した場合の
 * キー状態（usable / output-restricted 等）を返す。Chrome/Edge で有効。
 */
export async function probeHdcpStatus(): Promise<string> {
  try {
    if (typeof navigator === 'undefined' || !navigator.requestMediaKeySystemAccess) {
      return 'EME unavailable';
    }
    const access = await navigator.requestMediaKeySystemAccess('com.widevine.alpha', [
      {
        initDataTypes: ['cenc'],
        videoCapabilities: [
          { contentType: 'video/mp4; codecs="avc1.42E01E"', robustness: 'SW_SECURE_CRYPTO' },
        ],
      },
    ]);
    const mediaKeys = await access.createMediaKeys();
    const getStatusForPolicy = (mediaKeys as any).getStatusForPolicy;
    if (typeof getStatusForPolicy !== 'function') {
      return 'getStatusForPolicy unsupported';
    }
    return await getStatusForPolicy.call(mediaKeys, { minHdcpVersion: '1.4' });
  } catch (e) {
    return `probe failed: ${e instanceof Error ? e.message : String(e)}`;
  }
}

let emeKeyStatusHookInstalled = false;

/**
 * MediaKeys.createSession をラップし、生成された全セッションの
 * keystatuseschange を記録する。プレイヤー生成前（load 前）に呼ぶこと。
 * プロセス内で 1 度だけインストールされる。
 */
function installEmeKeyStatusHook(tag: string): void {
  if (emeKeyStatusHookInstalled) return;
  if (typeof MediaKeys === 'undefined' || !MediaKeys.prototype?.createSession) return;
  emeKeyStatusHookInstalled = true;

  const origCreateSession = MediaKeys.prototype.createSession;
  MediaKeys.prototype.createSession = function (
    this: MediaKeys,
    ...args: Parameters<MediaKeys['createSession']>
  ): MediaKeySession {
    const session = origCreateSession.apply(this, args);
    session.addEventListener('keystatuseschange', () => {
      const statuses: { keyId: string; status: string }[] = [];
      session.keyStatuses.forEach((status, keyId) => {
        statuses.push({ keyId: bufferToHex(keyId), status: String(status) });
      });
      console.info(`${tag} EME key statuses:`, statuses);
    });
    return session;
  };
}

function bufferToHex(buffer: BufferSource): string {
  const bytes = buffer instanceof ArrayBuffer
    ? new Uint8Array(buffer)
    : new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function logVideoElementState(tag: string, video: HTMLVideoElement): void {
  const quality = (video as any).getVideoPlaybackQuality?.();
  console.info(`${tag} video state:`, {
    readyState: video.readyState,
    networkState: video.networkState,
    currentTime: video.currentTime,
    videoWidth: video.videoWidth,
    videoHeight: video.videoHeight,
    paused: video.paused,
    muted: video.muted,
    error: video.error
      ? { code: video.error.code, message: video.error.message }
      : null,
    droppedVideoFrames: quality?.droppedVideoFrames,
    totalVideoFrames: quality?.totalVideoFrames,
  });
}
